import { useMemo } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	prefetchQuery,
	useQuery,
} from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type GqlSuccessResponse,
	performGqlFetch,
} from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';
import { useSiteContext } from '../site-id/index.tsx';

import type { SitePermissionsResponse, SitePermissionsVariables } from './types.ts';

const AgentPermissionsQuery = `
	query AgentPermissions($cloudId: ID!) {
		atlassianStudio_userSiteContext(cloudId: $cloudId) {
			... on AtlassianStudioUserSiteContextOutput {
				userPermissions {
					isAbleToCreateAgents
					isAbleToGovernAgents
				}
			}
		}
	}
`;

type UseAgentSitePermissionsProps = {
	siteId?: string;
	touchpointSource: string;
};

const fetchAgentSitePermissions = (siteId: string) =>
	performGqlFetch<SitePermissionsResponse, SitePermissionsVariables>({
		gqlQuery: AgentPermissionsQuery,
		variables: { cloudId: siteId },
	});

/**
 * Imperative prefetch for `useAgentSitePermissions`. Pre-populates the
 * `useQuery` cache so a later mount of the hook gets a cache hit and skips
 * its own fetch. Idempotent — safe to call on every render. Pass `onStart`,
 * `onSuccess`, `onError` to mirror the hook's UFO experience-tracker spans
 * from the warm callsite; they only fire when this call actually kicks off
 * a fetch.
 */
export const prefetchAgentSitePermissions = ({
	siteId,
	onStart,
	onSuccess,
	onError,
}: {
	siteId: string;
	onStart?: () => void;
	onSuccess?: (response: GqlSuccessResponse<SitePermissionsResponse>) => void;
	onError?: (error: ApiError) => void;
}): void => {
	prefetchQuery({
		queryKey: ['fetchSitePermissions', siteId],
		queryFn: () => fetchAgentSitePermissions(siteId),
		onStart,
		onSuccess,
		onError,
	});
};

export const useAgentSitePermissions = ({
	siteId: overrideSiteId,
	touchpointSource,
}: UseAgentSitePermissionsProps): {
	data: GqlSuccessResponse<SitePermissionsResponse> | null;
	error: ApiError | null;
	isFetching: boolean;
	refetch: () => Promise<GqlSuccessResponse<SitePermissionsResponse> | null>;
} => {
	const { siteId } = useSiteContext();
	const experienceTracker = useAIMateExperienceTracker({
		touchpointSource,
	});

	const siteIdToUse = overrideSiteId ?? siteId;

	const query = useQuery({
		queryKey: ['fetchSitePermissions', siteIdToUse],
		queryFn: async () => {
			experienceTracker.start({
				id: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
			});
			return await fetchAgentSitePermissions(siteIdToUse);
		},
		onSuccess: () => {
			experienceTracker.succeed({
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
			});
		},
		onError: (error) => {
			experienceTracker.fail({
				name: ExperienceName.FETCH_AGENT_SITE_PERMISSIONS,
				error,
				teamName: ROVO_AGENTS_FE_TEAM_NAME,
			});
		},
	});

	return query;
};

export const useUserSitePermissions = ({
	siteId,
	touchpointSource,
}: UseAgentSitePermissionsProps): {
	isAbleToCreateAgents: boolean;
	isAbleToGovernAgents: boolean;
	isFetchingPermissions: boolean;
} => {
	const { data: sitePermissions, isFetching: isFetchingSitePermissions } = useAgentSitePermissions({
		siteId,
		touchpointSource,
	});

	const { isAbleToCreateAgents, isAbleToGovernAgents, isFetchingPermissions } = useMemo(() => {
		const userPermissions = sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions;
		return {
			isAbleToCreateAgents: userPermissions?.isAbleToCreateAgents ?? false,
			isAbleToGovernAgents: userPermissions?.isAbleToGovernAgents ?? false,
			isFetchingPermissions: isFetchingSitePermissions,
		};
	}, [
		isFetchingSitePermissions,
		sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions,
	]);

	return { isAbleToCreateAgents, isAbleToGovernAgents, isFetchingPermissions };
};
